import React, { useState } from "react";
import {
  CardActions,
  Button,
  Typography,
  Grid,
  useMediaQuery,
  Dialog,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  DialogActions,
  DialogContent,
  Box,
  Card,
  CardHeader,
  CardContent,
  Tooltip,
} from "@material-ui/core";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";
import { AreaType, OutboundStatus } from "../../../utils/constants";
import { getUnitName } from "../../../utils/loadTypeUtils";
import { getInventoryPrimaryField } from "../../../utils/utils";
import { isTrack } from "../../../utils/locationUtils";
import { ExceptionIcon, ScannerIcon } from "../../../components/common/iconography";

const useStyles = makeStyles((theme) => ({
  link: {
    color: "inherit",
    textDecoration: "none",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(0),
  },
}));

export const InventorySearchCard = ({
  inventory,
  match,
  history,
  scannedBarcodes,
}) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  if (!inventory) return <></>;

  const getFullName = (productName, size = 26) => {
    if (!productName) return "";
    if (!isXSmall) return productName;
    if (productName.length <= size) return productName;
    return productName.substring(0, size - 3) + "...";
  };

  // Route on the clicked item's area, never the inventory header: the header
  // keeps the receiving shipment's area, so a stock import or a rail unload
  // leaves it on Track while the item itself sits in a yard location.
  //
  // An item on a track offers both of the actions below, because they are two
  // different jobs. Track Management moves the car and never changes where the
  // stock is recorded, while relocating is how the crew records the lumber they
  // unloaded out of that car by hand into a yard location. An item anywhere
  // else has no car to move, so it keeps the single Relocate action it has
  // always had.
  const handleLinkClick = (inventoryItem) => {
    history.push({
      pathname: match.url + "/relocate/" + inventoryItem.id,
      state: { scannedBarcodes },
    });
  };

  const handleTrackManagementClick = (inventoryItem) => {
    history.push({
      pathname: "/trackManagement",
      state: {
        selectedInventoryItem: inventoryItem,
        returnPath: match.url,
        scannedBarcodes,
      },
    });
  };

  const getPrimaryFieldTypgraphy = () => {
    const inventoryPrimaryField = getInventoryPrimaryField(
      inventory.account,
      inventory,
    );
    return (
      <Typography
        color="textSecondary"
        variant="body2"
      >{`${inventoryPrimaryField.label}: ${inventoryPrimaryField.value}`}</Typography>
    );
  };

  // True if this inventory item is on an outbound that is in progress (not PENDING, not released).
  const isItemOnActiveOutbound = (inventory, itm) => {
    const lineItems = inventory?.shipmentLineItems?.items ?? [];
    for (const sli of lineItems) {
      const outboundStatus = sli?.outboundShipment?.status;
      if (!outboundStatus) continue;
      if (
        outboundStatus !== OutboundStatus.PENDING &&
        outboundStatus !== OutboundStatus.RELEASED
      )
        if (
          (sli.inventoryItems?.items ?? []).some(
            (ii) => ii.inventoryItemId === itm.id,
          )
        )
          return true;
    }
    return false;
  };

  const isRelocateDisabled = (inventory, itm) => {
    if (itm.quantityCommitted > 0 && isItemOnActiveOutbound(inventory, itm))
      return true;
    const area = window.appUser.reloadYard.areas.items.find(
      (area) => area.id === itm.areaId,
    );
    return area?.areaType === AreaType.BULK;
  };

  // Both actions are spelled out rather than hidden behind a menu, since the
  // crew reads this on a phone; they stack on the narrow layout so each keeps a
  // full-width target. A relocate the rules refuse leaves Move Car alone,
  // because moving the car itself is still legitimate work.
  const getItemActions = (itm) => {
    const isItemOnTrack = isTrack(itm.areaId);
    return (
      <Box
        display="flex"
        flexDirection={isXSmall ? "column" : "row"}
        alignItems={isXSmall ? "stretch" : "center"}
      >
        {isItemOnTrack && (
          <Button
            size="small"
            onClick={() => handleTrackManagementClick(itm)}
            color="primary"
          >
            Move Car
          </Button>
        )}
        <Button
          size="small"
          onClick={() => handleLinkClick(itm)}
          disabled={isRelocateDisabled(inventory, itm)}
          color="primary"
        >
          {isItemOnTrack ? "Relocate Stock" : "Relocate"}
        </Button>
      </Box>
    );
  };

  const getCardHeader = () => {
    return isXSmall ? (
      <CardHeader
        style={{ margin: 0, paddingBottom: 0 }}
        title={
          <Box width={isXSmall ? 300 : 320}>
            <Typography variant="h6" noWrap>
              {inventory.productName}
            </Typography>
          </Box>
        }
      />
    ) : (
      <Typography className={classes.link} variant="h6" noWrap>
        {inventory.productName}
      </Typography>
    );
  };

  const handleTimelineClick = () => {
    history.push({ pathname: match.url + "/timeline/" + inventory.id });
  };

  const isItemFromScannedBarcode = (itm) => {
    if (!scannedBarcodes || scannedBarcodes.length === 0) {
      return true;
    }

    if (!Array.isArray(itm.barcodes?.items)) {
      return false;
    }

    return scannedBarcodes.every((scanned) =>
      itm.barcodes.items.some(
        (b) => scanned.value === b.value || scanned === b.value,
      ),
    );
  };

  return (
    <Grid item xs={12} md={3} lg={4} key={inventory.id}>
      <Card elevation={0} variant="outlined" style={{ width: "100%" }}>
        {getCardHeader()}
        <CardContent>
          {getPrimaryFieldTypgraphy()}
          <Typography color="textSecondary" variant="body2">
            Account Name: {getFullName(inventory.accountName)}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            Quantity Onhand: {inventory.quantityOnHand} {inventory.inventoryUOM}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            Date Received:{" "}
            {format(new Date(inventory.receivedAt), "MM/dd/yyyy")}
          </Typography>
          <Typography color="textSecondary" variant="body2">
            Product Code:{" "}
            {inventory.productCode ? inventory.productCode : "N/A"}
          </Typography>
        </CardContent>
        <CardActions>
          <Button color="secondary" onClick={() => setOpen(true)}>
            Locations
          </Button>
          <Button
            disabled={inventory.area.areaType === AreaType.BULK}
            onClick={handleTimelineClick}
          >
            Timeline
          </Button>
        </CardActions>
      </Card>
      <Dialog
        open={open}
        scroll={"paper"}
        fullWidth
        fullScreen={isXSmall ? true : false}
        maxWidth="md"
      >
        <Box
          component="span"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          m={2}
        >
          <Typography variant="h6">Locations</Typography>
          <CancelOutlinedIcon fontSize="large" onClick={() => setOpen(false)} />
        </Box>
        <DialogContent>
          {
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell>{isXSmall ? "Loc" : "Location"}</TableCell>
                    <TableCell>
                      {isXSmall ? "Onhand" : "Quantity Onhand"}
                    </TableCell>
                    {!isXSmall && <TableCell>Quantity Committed</TableCell>}
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory.inventoryItems.items
                    .filter((itm) => isItemFromScannedBarcode(itm))
                    .map((itm) => (
                      <TableRow key={itm.id}>
                        <TableCell
                          align="right"
                          style={{
                            padding: 0,
                            margin: 0,
                            width: 32,
                            paddingTop: 8,
                          }}
                        >
                          {scannedBarcodes?.length > 0 &&
                            scannedBarcodes.every((scanned) =>
                              itm.barcodes?.items?.some(
                                (b) =>
                                  scanned.value === b.value ||
                                  scanned === b.value,
                              ),
                            ) && (
                              <Tooltip title="All Scanned Match">
                                <ScannerIcon
                                  fontSize="small"
                                  style={{ color: "#4caf50" }}
                                />
                              </Tooltip>
                            )}
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{
                            padding: 0,
                            margin: 0,
                            width: 32,
                            paddingTop: 8,
                          }}
                        >
                          {itm.hasException ? (
                            <ExceptionIcon fontSize="small" />
                          ) : null}
                        </TableCell>
                        <TableCell>{itm.locationFullName}</TableCell>
                        <TableCell>{`${itm.quantityOnHand} ${
                          isXSmall ? "" : getUnitName(inventory.inventoryUOM)
                        }`}</TableCell>
                        {!isXSmall && (
                          <TableCell>{`${
                            itm.quantityCommitted ? itm.quantityCommitted : 0
                          } ${getUnitName(inventory.inventoryUOM)}`}</TableCell>
                        )}
                        <TableCell>{getItemActions(itm)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
